import React, { FormEventHandler, useCallback, useState } from 'react'

import clsx from 'clsx'

import BuildConfig from '$extensionSrc/buildConfig'
import ActionButton, {
  ActionButtonSize,
  ActionButtonType,
} from '$extensionSrc/components/ActionButton'
import Toast from '$extensionSrc/components/Toast'
import {
  enterprisePlanWelcomePath,
  selectPlanPath,
} from '$extensionSrc/consts/urlConsts'
import useForm from '$extensionSrc/hooks/useForm'
import { redeemEnterpriseBasicSignonStripeCodeMutation } from '$serverSrc/data/query'

import styles from './EnterpriseRedeemCodeForm.module.scss'
import FormSubmitButton from './FormSubmitButton'
import FormTextInput from './FormTextInput'
import { client } from '../apolloClient'

export default function EnterprisePlanRedeemCodeForm() {
  const defaultValues = { promotionCode: '' }
  const requiredValidations = {
    promotionCode: (code: string) => code.trim().length > 0,
  }

  const {
    getFieldChangeHandler,
    getRequiredValidationMetByField,
    isValid,
    values,
  } = useForm({
    defaultValues,
    requiredValidations,
  })

  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submitForm: FormEventHandler = useCallback(
    (event) => {
      event.preventDefault()
      setError(null)
      setIsSubmitting(true)

      client
        .mutate({
          mutation: redeemEnterpriseBasicSignonStripeCodeMutation,
          variables: { code: values.promotionCode },
        })
        .then((responsePayload) => {
          if (
            responsePayload?.data?.redeemEnterpriseBasicSignonStripeCode
              ?.success
          ) {
            window.location.assign(`${enterprisePlanWelcomePath}`)
          } else {
            setError('Not a valid code')
          }
        })
        .catch(() => {
          setError('Something went wrong. Please try again later.')
        })
        .finally(() => {
          setIsSubmitting(false)
        })
    },
    [values.promotionCode],
  )

  const onClickSkip = useCallback(() => {
    window.location.assign(selectPlanPath)
  }, [])

  return (
    <form className={styles.EnterpriseRedeemCodeForm} onSubmit={submitForm}>
      <div className={styles.formHeader}>
        <div className={clsx(styles.formTitle, 'bold', 'heading1')}>
          Get full access to {BuildConfig.TENANT_NAME} through your employer
        </div>
        <div className={clsx(styles.formSubtitle, 'body2')}>
          To redeem sponsored access, enter your license code.
        </div>
      </div>
      <div className={styles.formContent}>
        <FormTextInput
          placeholderText="Enter your license code"
          name="promotionCode"
          isSensitive={false}
          value={values.promotionCode}
          handleChange={(value) => {
            setError(null)
            getFieldChangeHandler('promotionCode')(value)
          }}
          requirements={[
            {
              text: 'Enter your license code',
              isFulfilled: getRequiredValidationMetByField('promotionCode'),
            },
          ]}
          disabled={isSubmitting}
        />
      </div>
      <div className={styles.formButtons}>
        <FormSubmitButton isValid={isValid && !isSubmitting}>
          Continue
        </FormSubmitButton>
        <ActionButton
          onClick={onClickSkip}
          type={ActionButtonType.TERTIARY}
          size={ActionButtonSize.MEDIUM}
        >
          I don't have sponsored access
        </ActionButton>
      </div>
      {error && <Toast text={error} noAutoDismiss showCloseIcon color="red" />}
    </form>
  )
}
